import {
  ActionIcon,
  AppShell,
  Badge,
  Box,
  Burger,
  Group,
  NavLink,
  MultiSelect,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useElementSize, useLocalStorage, useMediaQuery } from "@mantine/hooks";
import {
  IconActivity,
  IconAdjustments,
  IconChartDots3,
  IconChartHistogram,
  IconLogout,
  IconMoon,
  IconReportAnalytics,
  IconRobot,
  IconSettings,
  IconSun,
  IconVolume,
  IconVolumeOff,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";

import gmvpLogoUrl from "../../assets/gMVP_mark.png";
import { MarketSwitcher } from "./MarketSwitcher";
import { SyncButton } from "./SyncButton";
import { NavRail } from "./NavRail";
import { StatusPill } from "./ui";
import { fleetApi } from "../api/fleet";
import { useAgentSelector } from "../hooks/useAgentSelector";
import { useLiveSnapshot } from "../hooks/useLiveStream";
import { useGrafanaDashboards } from "../lib/grafanaDashboards";
import { useAuth } from "../hooks/useAuth";

type NavItem = {
  to: string;
  label: string;
  icon?: typeof IconActivity;
  children?: readonly NavItem[];
};

/**
 * Navigation is role-aware (plan §15): viewers see status/orders/positions
 * for their granted bots and the markets those bots trade; configuration,
 * simulations, fleet administration, users, incidents and operations are
 * administrator-only. The server enforces the same rules; this only hides.
 */
function buildNav(grafanaChildren: NavItem[], isAdmin: boolean): NavItem[] {
  const live: NavItem = { to: "/live", label: "Live Trading", icon: IconActivity };
  const marketData: NavItem = {
      to: "/market-data",
      label: "Market Data",
      icon: IconChartHistogram,
      children: [
        { to: "/market-data/current", label: "Current Market" },
        ...(isAdmin ? [{ to: "/market-data/manage", label: "Market Management" }, { to: "/market-data/collection", label: "Collection Health" }] : []),
      ],
    };
  // Bot Farm: per-bot pause / assignments / config live on each bot's
  // dashboard, fleet-wide actions in the master control panel on the Bot
  // Farm page — so they are not separate menu entries any more.
  const fleet: NavItem = {
      to: "/fleet",
      label: "Bot Farm",
      icon: IconRobot,
      children: [
        { to: "/fleet", label: "Bots" },
        ...(isAdmin ? [
          { to: "/fleet/signals", label: "Signals" },
          { to: "/fleet/incidents", label: "Incidents" },
          { to: "/fleet/channels", label: "Channels & Rules" },
        ] : [{ to: "/fleet/incidents", label: "Incidents" }]),
      ],
    };
  if (!isAdmin) return [live, marketData, fleet];
  return [
    live,
    marketData,
    {
      to: "/analytics",
      label: "Analytics",
      icon: IconReportAnalytics,
      children: [
        { to: "/analytics/current", label: "Current Market" },
        { to: "/analytics/all", label: "All Markets" },
        { to: "/analytics/manual-simulator", label: "Manual Simulator" },
        { to: "/analytics/automated-simulator", label: "Automated Simulator" },
        { to: "/analytics/saved-simulations", label: "Saved Simulations" },
      ],
    },
    fleet,
    {
      to: "/config",
      label: "Bot Config",
      icon: IconAdjustments,
      children: [
        { to: "/config/current", label: "Create New Config" },
        { to: "/config/previous", label: "Previous Configs" },
      ],
    },
    {
      to: "/grafana",
      label: "Grafana",
      icon: IconChartDots3,
      children: grafanaChildren.length > 0 ? grafanaChildren : undefined,
    },
    {
      to: "/settings",
      label: "Settings",
      icon: IconSettings,
      children: [
        { to: "/settings/general", label: "General" },
        { to: "/settings/kronos", label: "Kronos" },
        { to: "/settings/storage", label: "Storage" },
        // System administration (routes unchanged so bookmarks keep working).
        { to: "/fleet/users", label: "Users" },
        { to: "/fleet/notifications", label: "Notifications" },
        { to: "/fleet/audit", label: "Audit Log" },
        { to: "/fleet/operations", label: "Operations" },
      ],
    },
  ];
}

function matchesPath(pathname: string, to?: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** TanStack's <Link> sets aria-current="page" on any prefix match, and
 *  Mantine's NavLink styles that as active — so "/fleet" would light up on
 *  every /fleet/* page. Exact matching leaves highlighting to our logic. */
const EXACT = { exact: true } as const;

/** Phone header: brand row height and the padding under the wrapping toolbar. */
const MOBILE_BRAND_ROW_HEIGHT = 56;
const MOBILE_TOOLBAR_PAD = 8;

function hasActiveChild(pathname: string, item: NavItem): boolean {
  return (item.children ?? []).some((child) => matchesPath(pathname, child.to) || hasActiveChild(pathname, child));
}

/** The single most specific nav entry matching the URL across the whole
 *  menu, so exactly one parent and one child are highlighted (e.g.
 *  /fleet/users belongs to Settings, not to Bot Farm's "/fleet"). */
function bestNavMatch(pathname: string, nav: readonly NavItem[]): string | undefined {
  let best: string | undefined;
  const visit = (items: readonly NavItem[]) => {
    for (const it of items) {
      if (it.to && matchesPath(pathname, it.to) && (!best || it.to.length > best.length)) best = it.to;
      if (it.children) visit(it.children);
    }
  };
  visit(nav);
  return best;
}

type PathTree = { to?: string; children?: readonly PathTree[] };

function ownsPath(item: PathTree, to: string | undefined): boolean {
  if (!to) return false;
  return item.to === to || (item.children ?? []).some((c) => ownsPath(c, to));
}

/**
 * V3.1: global per-RPi agent selector, next to the MarketSwitcher.
 * Empty selection (default) shows fleet-wide data; picking one or more
 * agents scopes orders to those agents (ORed) and market data /
 * analytics to the union of their active configs for the current market
 * (see hooks/useAgentSelector).
 */
function AgentSelect() {
  const { agentIds, selectedAgents, agents, setAgentIds, isFiltering } = useAgentSelector();
  const label = selectedAgents.length === 1
    ? selectedAgents[0]!.display_name
    : `${selectedAgents.length} bots`;
  return (
    <Group gap={8} wrap="nowrap">
      <MultiSelect
        aria-label="Select bots"
        placeholder={isFiltering ? undefined : "All bots"}
        data={agents.map((a) => ({ value: a.id, label: a.display_name }))}
        value={agentIds}
        onChange={setAgentIds}
        clearable
        searchable={agents.length > 6}
        w={{ base: 150, sm: 240 }}
        radius="md"
        maxDropdownHeight={280}
        comboboxProps={{ withinPortal: true }}
      />
      {isFiltering && (
        <Badge variant="light" color="grape" radius="sm">
          viewing: {label}
        </Badge>
      )}
    </Group>
  );
}

export function Shell() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { location } = useRouterState();
  const { setColorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("dark", { getInitialValueInEffect: true });
  const { socket, botStatus, isOffline } = useLiveSnapshot();
  const [grafanaDashboards] = useGrafanaDashboards();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const nav = buildNav(
    grafanaDashboards.map((d) => ({ to: `/grafana/${d.id}`, label: d.title })),
    isAdmin,
  );
  const navMatch = bestNavMatch(location.pathname, nav);
  // Desktop only: the sidebar can fold into an icon rail (remembered per browser).
  const [navCollapsed, setNavCollapsed] = useLocalStorage<boolean>({
    key: "nav_collapsed",
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  const [audioEnabled, setAudioEnabled] = useLocalStorage<"on" | "off">({
    key: "live_audio_enabled",
    defaultValue: "on",
    getInitialValueInEffect: false,
  });
  // The rail is a desktop affordance; the mobile drawer always shows the full menu.
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, true, { getInitialValueInEffect: false }) ?? true;
  const railMode = navCollapsed && isDesktop;
  const audioOn = audioEnabled !== "off";
  const headerState = botStatus?.state ?? (socket.connected ? "running" : undefined);
  const headerMode = botStatus?.mode;
  const isDark = colorScheme === "dark";
  const mainBg = isDark ? theme.colors.dark[8] : "var(--mantine-color-slate-0)";

  const logout = async () => {
    try {
      await fleetApi.auth.logout();
    } catch {
      /* session may already be gone */
    }
    await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  };

  // Header controls are rendered once and placed differently per viewport:
  // desktop keeps everything on one 80px row; phones get the brand + icon
  // buttons on the first row and the data controls (sync / market / bots /
  // WS / status) in compact form on a second row that wraps as needed —
  // never sideways scrolling. The header height follows the measured
  // toolbar so the page content always starts below it.
  const { ref: toolbarRef, height: toolbarHeight } = useElementSize();
  const headerHeight = isDesktop ? 80 : MOBILE_BRAND_ROW_HEIGHT + Math.ceil(toolbarHeight) + MOBILE_TOOLBAR_PAD;
  const iconSize = isDesktop ? 42 : 34;
  const glyphSize = isDesktop ? 24 : 20;
  const dataControls = (
    <>
      <SyncButton compact={!isDesktop} />
      <MarketSwitcher compact={!isDesktop} />
      <AgentSelect />
      <Tooltip label={socket.lastError ?? (socket.connected ? "WS connected" : `WS disconnected (try ${socket.reconnectAttempt})`)}>
        <Text size="sm" fw={600} c={socket.connected ? "green" : "yellow"} style={{ whiteSpace: "nowrap" }}>
          {socket.connected ? "WS ●" : isDesktop ? `WS ○ (try ${socket.reconnectAttempt})` : "WS ○"}
        </Text>
      </Tooltip>
      {isOffline ? (
        <StatusPill state="offline" mode="—" />
      ) : (
        <StatusPill state={headerState} mode={headerMode} />
      )}
    </>
  );
  const iconControls = (
    <>
      <Tooltip label={audioOn ? "Win/loss sounds: on" : "Win/loss sounds: off"}>
        <ActionIcon
          variant="default"
          size={iconSize}
          radius="md"
          onClick={() => setAudioEnabled(audioOn ? "off" : "on")}
          aria-label="Toggle win/loss audio"
        >
          {audioOn ? <IconVolume size={glyphSize} /> : <IconVolumeOff size={glyphSize} />}
        </ActionIcon>
      </Tooltip>
      <ActionIcon
        variant="default"
        size={iconSize}
        radius="md"
        onClick={() => setColorScheme(isDark ? "light" : "dark")}
        aria-label="Toggle color scheme"
      >
        {isDark ? <IconSun size={glyphSize} /> : <IconMoon size={glyphSize} />}
      </ActionIcon>
      <Tooltip label="Sign out">
        <ActionIcon
          variant="default"
          size={iconSize}
          radius="md"
          onClick={() => void logout()}
          aria-label="Sign out"
        >
          <IconLogout size={glyphSize} />
        </ActionIcon>
      </Tooltip>
    </>
  );

  return (
    <AppShell
      header={{ height: headerHeight }}
      navbar={{ width: { base: 300, sm: navCollapsed ? 76 : 300 }, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding={{ base: "sm", sm: "lg" }}
    >
      <AppShell.Header>
        <Stack gap={0} h="100%">
          <Group h={isDesktop ? "100%" : MOBILE_BRAND_ROW_HEIGHT} px={{ base: "sm", sm: "lg" }} justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, overflow: "hidden" }}>
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="md" />
              <Group gap={10} wrap="nowrap" align="center">
                <Box
                  component="img"
                  src={gmvpLogoUrl}
                  alt="gMVP"
                  w={{ base: 40, sm: 56 }}
                  h={{ base: 40, sm: 56 }}
                  style={{ display: "block", flexShrink: 0, filter: "drop-shadow(0 3px 6px rgba(0, 0, 0, 0.45))" }}
                />
                <Title
                  order={2}
                  fz={{ base: "1.25rem", sm: "2.25rem" }}
                  style={{
                    fontFamily: '"Sixtyfour", system-ui, sans-serif',
                    fontWeight: 400,
                    letterSpacing: 1,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  gMVP<Box component="span" visibleFrom="xs"> v3.0</Box>
                </Title>
              </Group>
            </Group>
            <Group gap={isDesktop ? "md" : 4} wrap="nowrap">
              {isDesktop && dataControls}
              {iconControls}
            </Group>
          </Group>
          {!isDesktop && (
            <Box px="sm" pb={MOBILE_TOOLBAR_PAD}>
              <Group ref={toolbarRef} gap={8} wrap="wrap" align="center">
                {dataControls}
              </Group>
            </Box>
          )}
        </Stack>
      </AppShell.Header>

      <AppShell.Navbar p={railMode ? "xs" : "md"}>
        {railMode ? (
          <Stack align="center" gap="sm" py="xs">
            <Tooltip label="Expand menu" position="right" withArrow>
              <ActionIcon variant="subtle" size="lg" aria-label="Expand menu" onClick={() => setNavCollapsed(false)}>
                <IconLayoutSidebarLeftExpand size={20} />
              </ActionIcon>
            </Tooltip>
            <ScrollArea type="never">
              <NavRail
                items={nav}
                isActive={(item) => ownsPath(item, navMatch)}
                isChildActive={(_parent, child) => child.to === navMatch}
              />
            </ScrollArea>
          </Stack>
        ) : (<>
        <Stack gap="xs" pb="md">
          <Group justify="space-between" wrap="nowrap">
            <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ letterSpacing: 1 }}>
              Navigation
            </Text>
            <Tooltip label="Collapse menu" withArrow>
              <ActionIcon visibleFrom="sm" variant="subtle" size="sm" aria-label="Collapse menu" onClick={() => setNavCollapsed(true)}>
                <IconLayoutSidebarLeftCollapse size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Stack>
        <ScrollArea>
          <Stack gap={6}>
            {nav.map((item) => {
              const Icon = item.icon;
              const hasChildren = (item.children?.length ?? 0) > 0;
              const active = ownsPath(item, navMatch);
              if (!hasChildren) {
                return (
                  <NavLink
                    key={item.label}
                    component={Link}
                    activeOptions={EXACT}
                    to={item.to}
                    label={item.label}
                    onClick={close}
                    leftSection={Icon ? (
                      <ThemeIcon size={34} radius="md" variant={active ? "filled" : "light"}>
                        <Icon size={20} />
                      </ThemeIcon>
                    ) : undefined}
                    active={active}
                    styles={{
                      root: { borderRadius: 12, paddingInline: 10, paddingBlock: 8 },
                      body: { gap: 12 },
                      label: { fontSize: 16, fontWeight: 600 },
                    }}
                  />
                );
              }

              return (
                <NavLink
                  key={item.label}
                  component={Link}
                    activeOptions={EXACT}
                  to={item.to}
                  label={item.label}
                  leftSection={Icon ? (
                    <ThemeIcon size={34} radius="md" variant={active ? "filled" : "light"}>
                      <Icon size={20} />
                    </ThemeIcon>
                  ) : undefined}
                  active={active}
                  defaultOpened={active}
                  styles={{
                    root: { borderRadius: 12, paddingInline: 10, paddingBlock: 8 },
                    body: { gap: 12 },
                    label: { fontSize: 16, fontWeight: 600 },
                    children: { paddingTop: 6, paddingBottom: 4, paddingLeft: 44, display: "flex", flexDirection: "column", gap: 4 },
                  }}
                >
                  {item.children?.map((child) => (
                    <NavLink
                      key={child.label}
                      component={Link}
                    activeOptions={EXACT}
                      to={child.to!}
                      label={child.label}
                      onClick={close}
                      active={child.to === navMatch}
                      styles={{
                        root: { borderRadius: 10, paddingInline: 10, paddingBlock: 8 },
                        label: { fontSize: 14, fontWeight: 600 },
                      }}
                    />
                  ))}
                </NavLink>
              );
            })}
          </Stack>
        </ScrollArea>
        </>)}
      </AppShell.Navbar>

      <AppShell.Main style={{ backgroundColor: mainBg }}>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
